import React, { useState } from 'react'
import { useApi } from '../hooks/useApi'

interface ProcedureStep {
  id: number
  step_order: number
  title: string
  description: string | null
  default_owner: string | null
  typical_days: number | null
}

interface Procedure {
  id: number
  name: string
  description: string | null
  client_id: number | null
  source: string
  confirmed: boolean
  active: boolean
  created_at: string | null
  steps?: ProcedureStep[]
}

const BASE = '/api'

async function fetchProcedures(): Promise<Procedure[]> {
  const res = await fetch(`${BASE}/procedures/`, { credentials: 'include' })
  if (!res.ok) return []
  const data = await res.json()
  return data.procedures || []
}

async function fetchProcedure(id: number): Promise<Procedure | null> {
  const res = await fetch(`${BASE}/procedures/${id}`, { credentials: 'include' })
  if (!res.ok) return null
  return res.json()
}

async function confirmProcedure(id: number): Promise<void> {
  await fetch(`${BASE}/procedures/${id}/confirm`, { method: 'POST', credentials: 'include' })
}

export default function ProceduresPage() {
  const { data, loading, refresh } = useApi<Procedure[]>(fetchProcedures)
  const [expanded, setExpanded] = useState<number | null>(null)
  const [detail, setDetail] = useState<Procedure | null>(null)

  const handleExpand = async (id: number) => {
    if (expanded === id) {
      setExpanded(null)
      setDetail(null)
      return
    }
    setExpanded(id)
    const p = await fetchProcedure(id)
    setDetail(p)
  }

  const handleConfirm = async (id: number) => {
    await confirmProcedure(id)
    refresh()
  }

  if (loading) return <div className="loading">Loading playbooks...</div>
  if (!data || data.length === 0) {
    return (
      <div className="empty-state">
        <div className="message">No playbooks yet. Teach a process in chat — "here's how we do X..."</div>
      </div>
    )
  }

  return (
    <div>
      <div className="section-header">Playbooks ({data.length})</div>
      {data.map((p) => (
        <div key={p.id} className="feed-item" style={{ cursor: 'pointer' }} onClick={() => handleExpand(p.id)}>
          <div className="feed-meta">
            <span className="feed-type">{p.source}</span>
            {!p.confirmed && <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>unconfirmed</span>}
            {p.client_id && <span className="feed-client">client #{p.client_id}</span>}
          </div>
          <div className="headline">{p.name}</div>
          {p.description && <div className="body">{p.description}</div>}

          {expanded === p.id && detail && (
            <div style={{ marginTop: 12 }}>
              {detail.steps?.map((s) => (
                <div key={s.id} style={{ paddingLeft: 12, marginBottom: 8, borderLeft: '2px solid var(--border)' }}>
                  <div style={{ fontWeight: 500 }}>{s.step_order}. {s.title}</div>
                  {s.description && <div className="body">{s.description}</div>}
                  {s.default_owner && <div className="detail">Owner: {s.default_owner}</div>}
                  {s.typical_days && <div className="detail">~{s.typical_days}d</div>}
                </div>
              ))}
              {!p.confirmed && (
                <div className="feed-actions" style={{ marginTop: 8 }}>
                  <button onClick={(e) => { e.stopPropagation(); handleConfirm(p.id) }}>
                    Confirm playbook
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
